import React, { lazy, Suspense } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { PageLoader } from '../components/ui/PageLoader';
import { RequirePermission } from '../components/guards/RequirePermission';
import { useEstablishmentPermissions } from '../hooks/useEstablishmentPermissions';

const ManutencoesPage = lazy(() => import('../pages/manutencoes/ManutencoesPage'));
const ResidentManutencoesPage = lazy(() => import('../pages/manutencoes/ResidentManutencoesPage'));
const ManutencaoOverviewPage = lazy(() => import('../pages/manutencoes/ManutencaoOverviewPage'));
const MaintenanceHistoryPage = lazy(() => import('../pages/manutencoes/MaintenanceHistoryPage'));
const MaintenanceHistoryListPage = lazy(() => import('../pages/manutencoes/MaintenanceHistoryListPage'));
const MapaDeRiscoPage = lazy(() => import('../pages/manutencoes/MapaDeRiscoPage'));
const ResidentMaintenanceHistoryPage = lazy(() => import('../pages/manutencoes/ResidentMaintenanceHistoryPage'));
const ResidentMaintenanceHistoryListPage = lazy(() => import('../pages/manutencoes/ResidentMaintenanceHistoryListPage'));
const ConstructorProjectsRedirectPage = lazy(() => import('../pages/construtora/ConstructorProjectsRedirectPage'));

const CONDOMINIUM_PERMISSION = 'maintenance.condominium.access';
const RESIDENT_PERMISSION = 'maintenance.view.own';

/**
 * Manutenção do condomínio: plano, histórico e mapa de risco do prédio. O
 * morador não entra — o backend também recusa, este gate só evita a tela em
 * branco e o vazamento pela URL digitada.
 */
const Condominium: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequirePermission permission={CONDOMINIUM_PERMISSION}>
    <Suspense fallback={<PageLoader />}>{children}</Suspense>
  </RequirePermission>
);

/** Manutenção do apartamento do morador. */
const Resident: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequirePermission permission={RESIDENT_PERMISSION}>
    <Suspense fallback={<PageLoader />}>{children}</Suspense>
  </RequirePermission>
);

/**
 * `/manutencao-preventiva` sozinho não diz para qual visão ir. Mandar todo
 * mundo para `/condominio` jogava o morador direto no 403; aqui a entrada vai
 * para a visão que a pessoa realmente tem.
 */
const PreventiveMaintenanceEntry: React.FC = () => {
  const { hasPermission } = useEstablishmentPermissions();

  return (
    <Navigate
      to={
        hasPermission(CONDOMINIUM_PERMISSION)
          ? '/manutencao-preventiva/condominio/plano-de-manutencao'
          : '/manutencao-preventiva/condomino/plano-de-manutencao'
      }
      replace
    />
  );
};

/** Subrotas do morador que apenas filtram a mesma lista do apartamento. */
const RESIDENT_LIST_PATHS = [
  'plano-de-manutencao',
  'manutencoes-programadas',
  'manutencoes-fora-do-prazo',
  'manutencoes-dentro-do-prazo',
  'ar-condicionado',
  'sistema-de-gas',
  'piscina',
  'sauna',
  'eletrodomesticos',
  'hidraulico-vazamentos-torneiras-chuveiros-registros',
  'hidraulico-limpezas-de-sifoes-e-ralos',
  'hidraulico-verificar-pressoes-de-agua',
  'gerais-limpezas-de-persianas-trilhos-portas-de-correr',
  'gerais-inspecao-de-rejuntes-silicone-em-banheiros-e-cozinha',
  'gerais-pinturas-de-paredes-lubrificacao-de-dobradicas-e-trilhos',
];

/** Subrotas do condomínio que apenas filtram o mesmo plano do prédio. */
const CONDOMINIUM_LIST_PATHS = [
  'plano-de-manutencao',
  'manutencoes-programadas',
  'manutencoes-fora-do-prazo',
  'manutencoes-dentro-do-prazo',
  'elevadores',
  'sistemas-de-gas',
  'sistemas-de-combate-a-incendio',
];

/**
 * Chamada como função (não como componente) porque `<Routes>` só aceita
 * `<Route>` e fragmentos entre seus filhos.
 */
export const preventiveMaintenanceRoutes = (): React.ReactElement => (
  <>
    <Route path="/manutencao-preventiva" element={<PreventiveMaintenanceEntry />} />
    <Route
      path="/manutencao-preventiva/condominio"
      element={<Navigate to="/manutencao-preventiva/condominio/plano-de-manutencao" replace />}
    />
    <Route
      path="/manutencao-preventiva/condomino"
      element={<Navigate to="/manutencao-preventiva/condomino/plano-de-manutencao" replace />}
    />
    <Route
      path="/manutencao-preventiva/manutencao-corretiva"
      element={<Navigate to="/manutencao-corretiva/condominio" replace />}
    />

    {/* Condomínio */}
    {CONDOMINIUM_LIST_PATHS.map((path) => (
      <Route
        key={`condominio-${path}`}
        path={`/manutencao-preventiva/condominio/${path}`}
        element={<Condominium><ManutencoesPage /></Condominium>}
      />
    ))}
    <Route
      path="/manutencao-preventiva/condominio/historico-das-manutencoes"
      element={<Condominium><ManutencaoOverviewPage /></Condominium>}
    />
    <Route
      path="/manutencao-preventiva/condominio/historico-detalhado"
      element={<Condominium><MaintenanceHistoryListPage /></Condominium>}
    />
    <Route
      path="/manutencao-preventiva/condominio/mapa-de-risco"
      element={<Condominium><MapaDeRiscoPage /></Condominium>}
    />
    <Route
      path="/manutencao-preventiva/condominio/construtoras"
      element={<Condominium><ConstructorProjectsRedirectPage /></Condominium>}
    />
    <Route
      path="/manutencao-preventiva/maintenance/:maintenanceId/history"
      element={<Condominium><MaintenanceHistoryPage /></Condominium>}
    />

    {/* Morador. Estas subrotas já apontaram para ManutencoesPage — a página do
        CONDOMÍNIO — e entregavam o plano do prédio numa URL de morador. */}
    {RESIDENT_LIST_PATHS.map((path) => (
      <Route
        key={`condomino-${path}`}
        path={`/manutencao-preventiva/condomino/${path}`}
        element={<Resident><ResidentManutencoesPage /></Resident>}
      />
    ))}
    <Route
      path="/manutencao-preventiva/condomino/historico-das-manutencoes"
      element={<Resident><ResidentMaintenanceHistoryListPage /></Resident>}
    />
    <Route
      path="/manutencao-preventiva/condomino/maintenance/:maintenanceId/history"
      element={<Resident><ResidentMaintenanceHistoryPage /></Resident>}
    />
  </>
);
